import { Box, Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  CardComponent,
  ClaimDetail,
  Download,
  SvgTable,
  Table,
  ToggleGroup,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useRef, useState } from 'react';
import { SubDetailValueRenderer } from '../SubDetailValueRenderer';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { subDetailsItem } from '../../../pages/PaymentIntegrityPage/PaymentIntegrity.types';
import { ClaimDetailData } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import { medicalHistoryClaimIDMessage } from '../../../core-utils/messages/riskPatientDetailsConstants';
import properties from '../../../core-utils/properties';
import { useStores } from '../../../stores';
import { downloadCSV } from '../../../core-utils/helper-utils/downloadPdfHelper';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import { Layers } from '@mui/icons-material';
import Snackbar from '../Snackbar';
import axios from 'axios';
import { PATIENT_DETAILS } from '../../../core-utils/routes';
import { useNavigate } from 'react-router-dom';

export interface SubDetailsCardProps {
  claimDetails: subDetailsItem[];
}

const SubDetailsCard: React.FC<SubDetailsCardProps> = ({ claimDetails }) => {
  const { classes } = makeStyles()({
    subDetailsGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '2.5rem',
    },
    gridItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      maxWidth: '250px',
    },
    text: {
      textDecoration: 'dashed',
      textDecorationLine: 'underline',
      textDecorationThickness: '0.5px',
      textUnderlineOffset: '5px',
    },
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    tableHeader: {
      height: '48px',
      backgroundColor: patientAccessTheme.palette.grey[100],
      borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      '&:first-child': {
        paddingLeft: '32px',
      },
      textAlign: 'left',
    },
    tableRow: {
      height: '48px',
      padding: '0px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      '& td:first-child': {
        paddingLeft: '32px',
      },
      '& td': {
        padding: '1rem 0',
      },
    },
    overallGridStyles: {
      width: '100%',
      marginRight: '150px',
    },
    downloadButtonGrid: {
      marginTop: '20px',
      marginBottom: '20px',
    },
    downloadCSVGrid: {
      marginRight: '10px',
    },
    hide: {
      display: 'none',
    },
    headerText: {
      fontSize: '14px',
      fontWeight: 500,
      color: patientAccessTheme.palette.text.highemphasis,
    },
    button: {
      padding: '0px',
    },
    ellipses: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      width: '140px',
      whiteSpace: 'nowrap',
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
    },
  })();

  const navigate = useNavigate();
  const employerGroupStore = useStores().employerGroupStore;
  const memberSummaryStore = useStores().memberSummaryStore;
  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [claimDetail, setClaimDetail] = useState<ClaimDetailData>();
  const [claimId, setClaimId] = useState<string>('');
  const [selectedTabName, setSelectedTabName] = React.useState<
    'left' | 'right'
  >('left');

  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  const getTruncatedText = (label: string) => {
    return (
      <Typography
        fontSize="14px"
        fontType="regular"
        lineHeight="24px"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        className={classes.ellipses}
      >
        {label}
      </Typography>
    );
  };

  const handleCloseButton = () => {
    setOpenModal(false);
  };
  const getTypeHeader = (subType: string) => {
    switch (subType) {
      case 'Pharmacy':
        return 'Rx Claim Details';
      case 'Facility':
        return 'Medical Facility Claim Details';
      case 'Professional':
        return 'Medical Professional Claim Details';
      default:
        return 'NA';
    }
  };
  const closeButton = () => {
    return (
      <Button
        label={
          <Typography
            textColor={patientAccessTheme.palette.status.white}
            fontSize="12px"
            lineHeight="12px"
            fontType={'medium'}
          >
            {'Close'}
          </Typography>
        }
        variant="contained"
        color="primary"
        height="36px"
        width="180px"
        borderRadius="4px"
        className={classes.button}
        onClick={() => {
          setOpenModal(false);
        }}
      />
    );
  };

  const formatClaimIDBoxView = () => {
    const data: { label: string; value: any }[] = [];
    const outerColumnKeys = Object.keys(claimDetail?.ouputJsonForOuterColumn);
    const outerColumnValues = Object.values(
      claimDetail?.ouputJsonForOuterColumn,
    );
    for (let i = 0; i < outerColumnKeys.length; i++) {
      if (outerColumnValues[i] !== '' && outerColumnValues[i] !== '-') {
        data.push({
          label: outerColumnKeys[i],
          value: outerColumnValues[i],
        });
      }
    }

    return data;
  };

  const formatClaimIDTableDataView = () => {
    const arrayData: any = [];
    claimDetail?.lineItemsTable.forEach((lineItems) => {
      const line = JSON.parse(JSON.stringify(lineItems));
      arrayData.push(line);
    });
    return arrayData;
  };
  const formatClaimIDTableDownloadColumnView = () => {
    const values = Object.keys(claimDetail?.tableData[0]);
    const columnArray: {}[] = [];
    values.forEach((column: any) =>
      columnArray.push({
        Header: (
          <Typography className={classes.headerText} style={{ width: '180px' }}>
            {column}
          </Typography>
        ),
        Footer: column,
        accessor: column,
        Cell: ({ value }: any) => <>{getTruncatedText(value)}</>,
      }),
    );
    return columnArray;
  };

  const formatClaimIDTableColumnView = () => {
    const outerColumnKeys = Object.keys(claimDetail?.lineItemsTable[0]);
    return outerColumnKeys;
  };
  const handleDownloadCSV = async (
    downloadAnchorRef: React.RefObject<HTMLAnchorElement | null>,
  ) => {
    const url =
      properties.aiqBaseUrl +
      properties.membersBaseUrl +
      properties.claimDetailsCsv;
    const requestBody = {
      eg_nid: egNid,
      member_unique_id: '',
      claim_id: claimId,
    };

    downloadCSV(downloadAnchorRef, url, requestBody, claimId);
    openSnackbar(medicalHistoryClaimIDMessage.downloadCSVMessage, 'info', 5000);
  };
  const handleClaimClick = async (clickedClaimId: string) => {
    setClaimId(clickedClaimId);
    await getClaimDetailAPI(clickedClaimId);
    setOpenModal(true);
  };
  const handleMemberIdClick = (memberId: string) => {
    if (memberId) {
      memberSummaryStore.setPathDetails([
        [
          'text',
          'Payment Integrity',
          {
            color: patientAccessTheme.palette.primary[500],
            fontWeight: 500,
          },
          () => {
            navigate(-1);
          },
        ],
      ]);

      navigate(PATIENT_DETAILS, { state: { memberId } });
    }
  };

  const getClaimDetailAPI = async (claimId: string) => {
    const url =
      properties.aiqBaseUrl +
      properties.membersBaseUrl +
      properties.claimDetail;

    const requestBody = {
      eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
      claim_id: claimId,
    };

    try {
      const response = await axios.post(url, requestBody);
      setClaimDetail(response?.data?.data);
    } catch (error: any) {
      openSnackbar(error.response?.data?.error, 'error', 8000);
    }
  };

  return (
    <>
      <CardComponent
        title=""
        body={
          <Grid className={classes.subDetailsGrid}>
            {claimDetails.map((item, index) => (
              <Box key={index} className={classes.gridItem}>
                <Typography
                  fontSize="12px"
                  textColor={patientAccessTheme.palette.text.lowemphasis}
                >
                  {item.label}
                </Typography>

                <SubDetailValueRenderer
                  item={item}
                  onClaimClick={handleClaimClick}
                  onMemberClick={handleMemberIdClick}
                />
              </Box>
            ))}
          </Grid>
        }
        mainGridStyles={{ marginBottom: '1rem' }}
      />
      <Grid>
        {openModal && (
          <>
            <Modal
              open={openModal}
              onClose={handleCloseButton}
              className={classes.modal}
            >
              <CardComponent
                title={getTypeHeader(claimDetail?.subType!) + ' - ' + claimId}
                width={'900px'}
                height={'643px'}
                body={
                  <>
                    {selectedTabName === 'left' ? (
                      <Grid style={{ marginTop: '20px' }}>
                        <ClaimDetail
                          data={formatClaimIDBoxView()}
                          cardWidth="875px"
                          cardHeight="600px"
                          claimRowData={formatClaimIDTableDataView()}
                          columnsValue={formatClaimIDTableColumnView()}
                        />
                        <Grid
                          style={{
                            display: 'flex',
                            justifyContent: 'right',
                            margin: '0px 24px 5px 0px',
                            position: 'absolute',
                            right: '0px',
                            bottom: '0px',
                          }}
                        >
                          {closeButton()}
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid>
                        <Grid style={{ height: '470px', marginTop: '20px' }}>
                          <Table
                            onHoverRowData={() => {}}
                            classNameForTableHeaderRowTop={classes.tableHeader}
                            classNameForTableDataRow={classes.tableRow}
                            classNameForTableHeader={classes.tableHeader}
                            columns={formatClaimIDTableDownloadColumnView()}
                            data={claimDetail?.tableData}
                            controlledPageIndex={0}
                            overallGridStyles={classes.overallGridStyles}
                            isManualPagination={true}
                            tableHeight="470px"
                          />
                        </Grid>
                        <Grid
                          container
                          direction="row"
                          className={classes.downloadButtonGrid}
                          sx={{
                            justifyContent: 'flex-end',
                            alignItems: 'flex-end',
                          }}
                        >
                          <Grid className={classes.downloadCSVGrid}>
                            <a
                              className={classes.hide}
                              href="empty"
                              ref={downloadAnchorRef}
                            />
                            <Button
                              label={medicalHistoryClaimIDMessage.downloadCSV}
                              variant="outlined"
                              color="primary"
                              height="33px"
                              width="180px"
                              borderRadius="4px"
                              startIcon={<Download />}
                              onClick={() =>
                                handleDownloadCSV(downloadAnchorRef)
                              }
                            />
                          </Grid>
                          {/* Need to implement in future so commenting now */}
                          {/* <Grid item>
                            <Button
                              label={medicalHistoryClaimIDMessage.downloadExcel}
                              variant="outlined"
                              color="primary"
                              height="33px"
                              width="180px"
                              borderRadius="4px"
                              startIcon={<Download />}
                              onClick={() =>
                                handleDownloadExcel(downloadAnchorRef)
                              }
                            />
                          </Grid> */}
                          <Grid style={{ marginLeft: '10px' }}>
                            {closeButton()}
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </>
                }
                topRightItem={
                  <ToggleGroup
                    leftValue={medicalHistoryClaimIDMessage.LEFT_VALUE}
                    rightValue={medicalHistoryClaimIDMessage.RIGHT_VALUE}
                    leftIcon={
                      <Layers style={{ height: '16px', width: '16px' }} />
                    }
                    rightIcon={
                      <SvgTable
                        style={{
                          height: '16px',
                          width: '16px',
                        }}
                      />
                    }
                    setSelectedToggle={setSelectedTabName}
                    showBothLabel={true}
                  />
                }
              />
            </Modal>
          </>
        )}
      </Grid>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};

export default SubDetailsCard;
